import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
// icons
import { ImBook } from 'react-icons/im';
import { TiShoppingCart } from 'react-icons/ti';
import { RiHistoryLine } from 'react-icons/ri';
import { FaUsers } from 'react-icons/fa';
import { GiBroom } from 'react-icons/gi';
import { BiCalendarCheck } from 'react-icons/bi';
// context
import { useGlobalContext } from 'context/globalContext';
import { formatCurrency } from 'utils/currencyFormatter';
// helpers
import { convertDateToDDMMYYYY } from 'helpers';
// api
import { getStatisticsForMonthYear, getAllOrders } from 'api/orders';
// compoennts
import CustomTable from 'components/backoffice/customTable';
import DatePicker from 'components/backoffice/dateTimePicker';
import Button from 'components/backoffice/buttonCustom';
// store
import { useUserStore } from 'stores/userStore';

const HomePanel = () => {
  const { t } = useTranslation(['backoffice']);
  const { profile, firstName } = useUserStore();
  const [statisticsData, setStatisticsData] = useState({
    registeredUsers: 0,
    ordersPlaced: 0,
    totalSales: '0.00',
    availableBooks: 0,
    recentOrders: []
  });
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [noResults, setNoResults] = useState(true);
  const { setIsLoading } = useGlobalContext();

  const isLibraryUser = profile === 'library';

  const getStatisticsData = useCallback(async () => {
    // No cargar datos si el usuario es de tipo library
    if (isLibraryUser) {
      setIsLoading(false);
      return;
    }

    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const statisticsData = await getStatisticsForMonthYear(currentMonth, currentYear);
      if (statisticsData?.status === 200 && statisticsData?.data.length > 0) {
        const {
          data: [
            {
              registered_users: registeredUsers,
              orders_placed: ordersPlaced,
              total_sales: totalSales,
              available_books: availableBooks
            }
          ]
        } = statisticsData;
        setStatisticsData(prevData => ({
          ...prevData,
          registeredUsers,
          ordersPlaced,
          totalSales,
          availableBooks
        }));
      }
      const recentSales = await getAllOrders({
        page: 1,
        page_size: 20,
        month: new Date(selectedMonthYear).getMonth() + 1 || '',
        year: new Date(selectedMonthYear).getFullYear() || '',
        estado: 2
      });
      if (recentSales?.status === 200 && recentSales?.data?.results?.length > 0) {
        setStatisticsData(prevData => ({
          ...prevData,
          recentOrders: recentSales?.data?.results
        }));
        setNoResults(false);
      } else {
        setStatisticsData(prevData => ({
          ...prevData,
          recentOrders: []
        }));
        setNoResults(true);
      }
    } catch (exception) {
      console.log(exception);
    }
  }, [isLibraryUser, selectedMonthYear, setIsLoading]);

  useEffect(() => {
    getStatisticsData();
    setIsLoading(false)
  }, [getStatisticsData, setIsLoading]);

  const cardsData = [
    { icon: FaUsers, text: `${t("registeredUsers")}`, value: statisticsData.registeredUsers },
    { icon: TiShoppingCart, text: `${t("ordersPlaced")}`, value: statisticsData.ordersPlaced },
    { icon: RiHistoryLine, text: `${t("totalSales")}`, value: formatCurrency(statisticsData.totalSales) },  // Usando formatCurrency
    { icon: ImBook, text: `${t("availableBooks")}`, value: statisticsData.availableBooks }
  ];

  const colsHome = [t('promoCodes.code'), t('date'), t('user'), t('orderAmount'), t('numberArticles')];

  const handleselectedMonthYear = (date) => {
    setSelectedMonthYear(date);
  }

  const handleClean = () => {
    setSelectedMonthYear('');
  }

  // Si el usuario es de tipo library, mostrar contenido genérico
  if (isLibraryUser) {
    const fullName = `${firstName || ''}`.trim() || t('user');

    return (
      <Row className="homeRow">
        <Col lg={12} className="px-4">
          <h3 className="w-100 text-start my-2 text-light">
            {t("bienvenidoPanel", { user: fullName })}
          </h3>
        </Col>
        <Col lg={12} className="mt-4 px-lg-4 px-2">
          <Card className="p-5 d-flex flex-column align-items-center card-overlay">
            <ImBook size={60} className="mb-3 text-light" />
            <h4 className="text-center text-light mb-3">
              {t("panelBiblioteca")}
            </h4>
            <p className="text-center text-light">
              {t("mensajePanelBiblioteca")}
            </p>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="homeRow">
      {/* <Col lg={12} className="px-4">
        <h3 className="w-100 text-start my-2 text-light">
          {t("resumen")}
        </h3>
      </Col>*/}
      {/* <Col lg={12} className="mt-3 px-lg-4 px-2">
        <Row>
          {cardsData.map(({ icon: Icon, text, value }, index) => (
            <Col lg={6} className="mb-3" key={`card-${index}`}>
              <Card className="p-3 d-flex flex-column align-items-center card-overlay">
                <Icon size={40} className="mb-2" />
                <h5 className="text-center">{text}</h5>
                <h3 className="text-center">{value}</h3>
              </Card>
            </Col>
          ))}
        </Row>
      </Col> */}
      <Col lg={12} className="mt-3 px-lg-4 px-2">
        <Row>
          <Col lg={6} xs={12} className='d-flex align-items-center'>
            <h3 className="w-100 text-start text-light m-0">
              {t("ventasRecientes")}
            </h3>
          </Col>
          <Col lg={6} xs={12}>
            <div className="d-flex gap-3 custom-width-datepicker responsive-datepicker">
              <DatePicker
                className="input-control form-control w-100"
                value={selectedMonthYear ? new Date(selectedMonthYear) : ''}
                onChange={handleselectedMonthYear}
                dateFormat="MM/yyyy"
                showMonthYearPicker={true}
                placeholderText={t('filterByMonthYear')}
                showIcon={true}
                icon={<BiCalendarCheck />}
              />
              <div className='mt-2'>
                <Button onClick={handleClean} color="terciary">
                  <GiBroom />
                </Button>
              </div>
            </div>
          </Col>
          <Col lg={12} className="justify-content-center">
            <div className="table-responsive">
              <CustomTable
                cols={colsHome}
                className="mt-2"
                noResults={noResults}>
                {statisticsData.recentOrders.map((order, index) => (
                  <tr key={index}>
                    <td>{order.code}</td>
                    <td>{convertDateToDDMMYYYY(order.fecha)}</td>
                    <td>{order.user_email || '-'}</td>
                    <td>{formatCurrency(order.total_order)}</td>
                    <td className="text-end">{order.num_elements}</td>
                  </tr>
                ))}
              </CustomTable>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default HomePanel;
